import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { useMediaQuery } from '@mui/material';
import { useLazyGetVideosQuery } from '@videos/services';
import { IVideoDto, TabValues } from '@videos/types';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useOverviewVideos = () => {
    const [searchParams] = useSearchParams();
    const selectedTab = useMemo(() => {
        const currentTab = searchParams.get('tab') ?? TabValues.LATEST;
        return currentTab === TabValues.LATEST || currentTab === TabValues.MOST_VIEWED ? currentTab : TabValues.LATEST;
    }, [searchParams]);

    const isLg = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const isMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const isSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const pageSize = isLg ? 16 : isMd ? 12 : isSm ? 8 : 4;

    const scrollElement = useMaxWidthWrapperScrollElement();

    const [getVideos, { isError: isGetVideosError }] = useLazyGetVideosQuery();

    const loadVideos = useCallback(
        async ({
            page,
            pageSize,
            highlighted,
            ordering,
        }: {
            page: number;
            pageSize: number;
            highlighted?: boolean;
            ordering?: string;
        }) => {
            const response = await getVideos(
                {
                    page,
                    page_size: pageSize,
                    highlighted,
                    ordering,
                },
                true
            ).unwrap();

            return response;
        },
        [getVideos]
    );

    const {
        items: latestVideos,
        sentinelRef: latestVideosSentinelRef,
        hasRealItems: hasLatestVideosRealItems,
        isLoadingMore: isLoadingMoreLatestVideos,
    } = useInfiniteLazyLoader<IVideoDto>({
        pageSize,
        scrollElement,
        contextKey: `latest-videos:${pageSize}`,
        loadPage: loadVideos,
        getItemKey: (video) => video.youtube_video_id,
        onError: (error) => {
            console.error('Error loading latest videos:', error);
        },
        enabled: selectedTab === TabValues.LATEST,
        scrollToTopOnReset: false,
    });

    const {
        items: mostViewedVideos,
        sentinelRef: mostViewedVideosSentinelRef,
        hasRealItems: hasMostViewedVideosRealItems,
        isLoadingMore: isLoadingMoreMostViewedVideos,
    } = useInfiniteLazyLoader<IVideoDto>({
        pageSize,
        scrollElement,
        contextKey: `most-viewed-videos:${pageSize}`,
        loadPage: (params) => loadVideos({ ...params, ordering: '-view_count' }),
        getItemKey: (video) => video.youtube_video_id,
        onError: (error) => {
            console.error('Error loading most viewed videos:', error);
        },
        enabled: selectedTab === TabValues.MOST_VIEWED,
        scrollToTopOnReset: false,
    });

    return {
        latest: {
            videos: latestVideos,
            sentinelRef: latestVideosSentinelRef,
            hasRealItems: hasLatestVideosRealItems,
            isLoadingMore: isLoadingMoreLatestVideos,
        },
        mostViewed: {
            videos: mostViewedVideos,
            sentinelRef: mostViewedVideosSentinelRef,
            hasRealItems: hasMostViewedVideosRealItems,
            isLoadingMore: isLoadingMoreMostViewedVideos,
        },
        isGetVideosError,
    };
};
